import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function Chambres() {
  const [chambres, setChambres] = useState([]);
  const [postulations, setPostulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newChambre, setNewChambre] = useState({ numero: '', bloc: '', capacite_max: 4 });

  // Utilisation de la configuration centralisée
  const API_BASE = config.apiBase;

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    
    try {
      // Récupérer les chambres
      const resChambres = await fetch(`${API_BASE}/admin/chambres/lister.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const dataChambres = await resChambres.json();
      if (dataChambres.success) setChambres(dataChambres.chambres);
      
      // Récupérer les postulations
      const resPostulations = await fetch(`${API_BASE}/admin/postulations/lister.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const dataPostulations = await resPostulations.json();
      if (dataPostulations.success) setPostulations(dataPostulations.postulations);
      
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const creerChambre = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_BASE}/admin/chambres/creer.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newChambre)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Chambre créée');
        setShowForm(false);
        setNewChambre({ numero: '', bloc: '', capacite_max: 4 });
        fetchData();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const validerPostulation = async (id) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_BASE}/admin/postulations/valider.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id_postulation: id })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Postulation validée');
        fetchData();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-600">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-primary">Gestion des chambres</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-opacity-90"
        >
          + Ajouter une chambre
        </button>
      </div>

      {/* Formulaire d'ajout de chambre */}
      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg font-semibold mb-4">Nouvelle chambre</h2>
          <form onSubmit={creerChambre} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="Numéro (ex: 101)"
              value={newChambre.numero}
              onChange={(e) => setNewChambre({ ...newChambre, numero: e.target.value })}
              className="border rounded-lg px-3 py-2"
              required
            />
            <input
              type="text"
              placeholder="Bloc (ex: A)"
              value={newChambre.bloc}
              onChange={(e) => setNewChambre({ ...newChambre, bloc: e.target.value })}
              className="border rounded-lg px-3 py-2"
              required
            />
            <input
              type="number"
              placeholder="Capacité max"
              value={newChambre.capacite_max}
              onChange={(e) => setNewChambre({ ...newChambre, capacite_max: parseInt(e.target.value) })}
              className="border rounded-lg px-3 py-2"
              required
            />
            <div className="md:col-span-3 flex gap-2">
              <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg">Créer</button>
              <button type="button" onClick={() => setShowForm(false)} className="bg-gray-400 text-white px-4 py-2 rounded-lg">Annuler</button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des postulations */}
      {postulations.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Postulations en attente ({postulations.length})</h2>
          <div className="space-y-3">
            {postulations.map((p) => (
              <div key={p.id_postulation} className="bg-white p-4 rounded-lg shadow-md flex justify-between items-center">
                <div>
                  <p className="font-semibold">{p.prenom} {p.nom}</p>
                  <p className="text-sm text-gray-600">{p.email}</p>
                  <p className="text-sm text-gray-500">Chambre proposée: {p.chambre_bloc} - N°{p.chambre_numero}</p>
                </div>
                <button
                  onClick={() => validerPostulation(p.id_postulation)}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Valider
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tableau des chambres */}
      <h2 className="text-xl font-semibold mb-4">Liste des chambres</h2>
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bloc</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numéro</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Capacité</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Occupée</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Places libres</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">État</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {chambres.map((c) => (
              <tr key={c.id_chambre} className="hover:bg-gray-50">
                <td className="px-6 py-4">{c.bloc}</td>
                <td className="px-6 py-4 font-mono">{c.numero}</td>
                <td className="px-6 py-4">{c.capacite_max}</td>
                <td className="px-6 py-4">{c.occupation_actuelle}</td>
                <td className="px-6 py-4">{c.capacite_max - c.occupation_actuelle}</td>
                <td className="px-6 py-4">
                  {c.occupation_actuelle < c.capacite_max ? (
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">Disponible</span>
                  ) : (
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">Pleine</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}